import { FC } from 'react';
import {
  useGetApprovedAmount,
  useGetApprovingLender,
  useGetConsumer,
} from '../services/flowManager';
import {
  LinkFlow,
  useSetLinkInteraction,
  getInitialDeliveryMethodByFlow,
} from '../services/sendLinkManager';
import {
  useGetInteractionOptions,
  useGetMerchantConfigurationOptions,
} from '../services/configManager';
import { MuiTrackerButton } from './TrackerButtonWrapper';
import {
  useGetApplyConfiguration,
  useGetCheckoutConfiguration,
} from '../services/lendersConfigurationManager';
import { useSetCurrentModal } from '../services/modalManager';
import useFeedbackSnackbar from '../hooks/useFeedbackSnackbar';
import { FEEDBACK_TYPE } from '../services/feedbackManager';
import { Typography } from '@mui/material';
import { useIsCartInvalid } from '../hooks/useIsCartInvalid';
import { useCartLimitationModal } from '../modals/useCartLimitationModal';
type SendLinkButtonProps = {
  action: LinkFlow;
};
const SendLinkButton: FC<SendLinkButtonProps> = ({ action }) => {
  const updateCurrentModal = useSetCurrentModal();
  const openLinkModal = () => updateCurrentModal('sendLink');
  const openNoteModal = () => updateCurrentModal('checkoutLenderDisabled');
  const { citiExperience } = useGetMerchantConfigurationOptions();
  const merchantInteractionOptions = useGetInteractionOptions();
  const updateLinkInteraction = useSetLinkInteraction();
  const approvedAmount = useGetApprovedAmount();
  const consumer = useGetConsumer();
  const selectedLender = useGetApprovingLender();
  const checkoutConfig = useGetCheckoutConfiguration();
  const applyConfig = useGetApplyConfiguration();
  const isCheckout = action !== LinkFlow.Apply;
  const [feedback] = useFeedbackSnackbar();
  const isCartInvalid = useIsCartInvalid();
  const getCartLimitation = useCartLimitationModal();

  const expressCheckoutAllowed =
    merchantInteractionOptions.expressCheckoutLink ||
    merchantInteractionOptions.expressCheckoutQrCode ||
    merchantInteractionOptions.expressCheckoutSms;

  const shouldDisableSendLink =
    (isCheckout && isCartInvalid) ||
    (!expressCheckoutAllowed && !approvedAmount && !consumer);

  const handleSendLink = async () => {
    if ((isCheckout && !checkoutConfig) || (!isCheckout && !applyConfig)) {
      feedback({ type: FEEDBACK_TYPE.GENERAL_ERROR });
      return;
    }

    if (isCheckout && checkoutConfig) {
      const checkoutAllowed =
        !selectedLender ||
        checkoutConfig.lendersConfigurations!.find(
          (lender) => lender.id === selectedLender,
        );

      if (!checkoutAllowed) {
        openNoteModal();
        return;
      }

      const shouldContinue = await getCartLimitation();
      if (!shouldContinue) {
        return;
      }
    }

    updateLinkInteraction({
      deliveryMethod: getInitialDeliveryMethodByFlow(
        action,
        merchantInteractionOptions,
      ),
      action,
    });
    openLinkModal();
  };

  const buttonActionText = action === LinkFlow.Apply ? 'Apply' : 'Checkout';
  return (
    <MuiTrackerButton
      buttonName={`send-${action.toLowerCase()}-link`}
      fullWidth
      variant={isCheckout ? 'outlinedSecondary' : 'text'}
      data-testid={`send-${
        action === LinkFlow.Apply ? 'apply' : 'checkout'
      }-link-btn`}
      onClick={handleSendLink}
      disabled={shouldDisableSendLink}
    >
      <Typography variant="inherit">
        {citiExperience
          ? `QR Code - ${buttonActionText}`
          : "On customer's device"}
      </Typography>
    </MuiTrackerButton>
  );
};
export default SendLinkButton;
